<template>
  <div class="rec-ref__row-wrap">
    <span
      v-for="level in depth"
      :key="'guide-' + level"
      class="rec-ref__guide"
      :style="{ left: guideLineLeft(level - 1) + 'px' }"
    />
    <div
      ref="rowEl"
      class="rec-ref__row"
      :class="{ 'rec-ref__row--active': active }"
      :style="{ paddingLeft: `${depth * 22}px` }"
      @click.stop.prevent="onRowClick"
    >
    <span
      v-if="hasChildren"
      class="rec-ref__chevron"
      :class="{ 'rec-ref__chevron--closed': !expanded }"
      @click.stop.prevent="emit('toggleExpand', rowKey)"
    >
      <svg class="rec-ref__chevron-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
    <span v-else class="rec-ref__chevron-placeholder"></span>

    <mosta-checkbox
      v-if="showCheckbox"
      class="rec-ref__check"
      :model-value="selected"
      @update:model-value="onCheckboxUpdate"
      @click.stop
    />

    <div class="rec-ref__label">
      <span v-for="(span, index) in nameSpans" :key="index" :style="span.style">{{ span.text }}</span>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import { VariableType } from '@/models/etc/types';
import { buildHighlightSpans } from './composables/useRecursiveSearchMap';
import { isNodeSelected, toggleSelection } from './composables/useRecursiveSelection';
import { treeGuideLineLeft as calcTreeGuideLineLeft } from '@/components/common/tree/treeGuideLines';

function guideLineLeft(level: number): number {
  return calcTreeGuideLineLeft(level);
}

const props = withDefaults(defineProps<{
  node: any;
  modelValue: any[];
  rowKey: string;
  depth: number;
  hasChildren: boolean;
  hasRawChildren: boolean;
  expanded: boolean;
  active: boolean;
  label: string;
  tokens: string[];
  allowMultiple?: boolean;
  takeIdsOnly?: boolean;
  hideCheckboxes?: boolean;
  showRecords?: boolean;
  isColumn?: boolean;
  returnType?: string;
  elementType?: VariableType;
  nestedKeyName: string;
  entityInsideName?: string;
  chain: any[];
}>(), {
  allowMultiple: false,
  takeIdsOnly: false,
  hideCheckboxes: false,
  showRecords: true,
  isColumn: false,
  returnType: 'single-element',
  elementType: VariableType.Entity,
  entityInsideName: '',
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void;
  (e: 'toggleExpand', key: string): void;
  (e: 'elementToggled', value: boolean): void;
  (e: 'rowMounted', payload: { key: string; el: HTMLElement | null }): void;
}>();

const rowEl = ref<HTMLElement | null>(null);
watch(rowEl, (el) => emit('rowMounted', { key: props.rowKey, el }), { immediate: true });

// Признак того, что узел в принципе выбираемый по семантике дерева
// (лист, либо разворачиваемая ветка в режиме без records). Не зависит от
// видимости чекбокса — нужен и для клика по строке при hideCheckboxes=true.
const isSelectable = computed(() => {
  if (props.returnType === 'none') return !!props.node?.Selectable || (props.hasChildren && !props.showRecords);
  return !props.hasRawChildren || (props.hasChildren && !props.showRecords);
});

const showCheckbox = computed(() => {
  if (props.hideCheckboxes) return false;
  return isSelectable.value;
});

const selected = computed(() => isNodeSelected(props.node, props.modelValue, {
  returnType: props.returnType,
  isColumn: props.isColumn,
  elementType: props.elementType,
  takeIdsOnly: props.takeIdsOnly,
  entityInsideName: props.entityInsideName,
  chain: props.chain,
}));

const nameSpans = computed(() => buildHighlightSpans(props.label || '', props.tokens || []));

function updateSelection(checked: boolean) {
  const next = toggleSelection(props.node, checked, props.modelValue || [], {
    returnType: props.returnType,
    allowMultiple: props.allowMultiple,
    chain: props.chain,
    nestedKeyName: props.nestedKeyName,
    isColumn: props.isColumn,
    elementType: props.elementType,
    takeIdsOnly: props.takeIdsOnly,
    entityInsideName: props.entityInsideName,
  });

  // in none mode we still emit cloned model to keep parent watchers compatible
  emit('update:modelValue', props.returnType === 'none' ? [...(props.modelValue || [])] : next);
  emit('elementToggled', checked);
}

function onCheckboxUpdate(value: any) {
  updateSelection(!!value);
}

function onRowClick() {
  // По клику на подпись: если есть шеврон — ведём себя как клик по шеврону.
  if (props.hasChildren) {
    emit('toggleExpand', props.rowKey);
    return;
  }
  // Клик по строке всегда переключает выбор для выбираемых узлов,
  // даже если чекбокс скрыт (legacy-поведение, нужно для FormulaSelector).
  if (!isSelectable.value) return;
  updateSelection(!selected.value);
}

function toggle() {
  updateSelection(!selected.value);
}

function scrollIntoView(viewport?: HTMLElement | null) {
  if (!rowEl.value) return;
  rowEl.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (!viewport) return;
}

defineExpose({ toggle, scrollIntoView });
</script>

<style lang="scss" scoped>
@import './styles/_recursive-selector.scss';
</style>
